import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  StopIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  MagnifyingGlassPlusIcon,
  VideoCameraIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { videoApi } from '@/services/videoApi';
import YouTubePublishModal from './YouTubePublishModal';
import StepDetailModal from './StepDetailModal';

interface VideoTranslationProgressProps {
  jobId: string;
  onCancel: () => void;
  onComplete: () => void;
  onClose?: () => void;
}

const VideoTranslationProgress: React.FC<VideoTranslationProgressProps> = ({
  jobId,
  onCancel,
  onComplete,
  onClose
}) => {
  const queryClient = useQueryClient();

  // Step currently open in the expanded detail modal (view/edit/rerun)
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const { data: job, isLoading } = useQuery({
    queryKey: ['video-job', jobId],
    queryFn: () => videoApi.getJob(jobId),
    refetchInterval: (data) => {
      const s = data?.job?.status;
      if (s === 'processing' || s === 'pending') return 2000;
      // Slow background poll while paused for review, so the popup still picks
      // up the transition back to 'processing' on its own even if the
      // invalidateQueries() fired by the approve/continue actions is ever missed.
      if (s === 'awaiting_review') return 5000;
      return false;
    },
    enabled: !!jobId
  });

  // Resumes the pipeline from a paused ('awaiting_review') state — used both by
  // the translation-review gate ("Looks good, continue as-is") and by the
  // generic "paused after an isolated step run" banner ("Continue pipeline from here").
  const continuePipelineMutation = useMutation({
    mutationFn: (stepId: string) => videoApi.continueStep(jobId, stepId),
    onSuccess: () => {
      toast.success('Continuing pipeline');
      queryClient.invalidateQueries({ queryKey: ['video-job', jobId] });
    },
    onError: () => toast.error('Failed to continue'),
  });

  React.useEffect(() => {
    const s = job?.job?.status;
    if (s === 'completed' || s === 'cancelled') {
      onComplete();
    }
  }, [job?.job?.status, onComplete]);

  if (isLoading || !job) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const { job: jobData } = job;
  const isProcessing = jobData.status === 'processing' || jobData.status === 'pending';
  const isAwaitingReview = jobData.status === 'awaiting_review';
  const isCompleted = jobData.status === 'completed';
  const isFailed = jobData.status === 'failed';
  const isCancelled = jobData.status === 'cancelled';
  const translationStep = jobData.steps?.find((s: any) => s.id === 'step-4');

  // 'awaiting_review' is used both by the translation-review gate (always
  // pauses right after step-4) and by isolated single-step reruns (pauses
  // right after whichever step the user chose to "run alone"). Figure out
  // which one this is: the paused-after step is whichever completed step has
  // the highest order (everything before it is done, everything after is
  // still pending).
  const sortedSteps = [...(jobData.steps || [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
  const completedSteps = sortedSteps.filter((s: any) => s.status === 'completed');
  const pausedAfterStep = isAwaitingReview && completedSteps.length > 0
    ? completedSteps[completedSteps.length - 1]
    : null;
  const isTranslationReviewPause = isAwaitingReview
    && !!jobData.request?.requireTranslationReview
    && pausedAfterStep?.id === 'step-4';

  const getStepIcon = (step: any) => {
    switch (step.status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Video Translation Progress
        </h3>

        <div className="flex items-center gap-2">
          {(isProcessing || isAwaitingReview) && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Cancel this translation job? Any running processes will be stopped.')) {
                  onCancel();
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            >
              <StopIcon className="h-4 w-4" />
              <span>Cancel</span>
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {jobData.currentStep}
          </span>
          <span className={`text-sm font-medium ${getStatusColor(jobData.status)}`}>
            {jobData.status.charAt(0).toUpperCase() + jobData.status.slice(1)}
          </span>
        </div>

        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              isFailed ? 'bg-red-500' : isCompleted ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${jobData.progress}%` }}
          ></div>
        </div>

        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>{jobData.progress}% complete</span>
          <span>Job ID: {jobData.id}</span>
        </div>
      </div>

      {/* Translation review gate. Hidden while the step modal is open for step-4 —
          that modal has its own "Save & continue" action, and letting both this
          banner and the modal fire a continue at once would race two pipeline
          runs against the same output files. */}
      {isTranslationReviewPause && translationStep && expandedStep !== 'step-4' && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">
            Review the translated text before continuing
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-3">
            You chose to verify the translation before it&apos;s converted to speech. Open the Text Translation step to read or edit the text, then approve to continue.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={continuePipelineMutation.isLoading}
              onClick={() => setExpandedStep('step-4')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              <EyeIcon className="h-4 w-4" />
              Review translated text
            </button>
            <button
              type="button"
              disabled={continuePipelineMutation.isLoading}
              onClick={() => continuePipelineMutation.mutate('step-4')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 text-sm font-medium rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50"
            >
              {continuePipelineMutation.isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
              Looks good, continue as-is
            </button>
          </div>
        </div>
      )}

      {/* Generic pause banner — shown after an isolated "run this step only" rerun
          (any step, not just translation review). Hidden while that step's own
          modal is open, same race-avoidance reasoning as above. */}
      {isAwaitingReview && !isTranslationReviewPause && pausedAfterStep && expandedStep !== pausedAfterStep.id && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
            Paused after {pausedAfterStep.name}
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
            This step ran on its own without continuing automatically. Check the result, then continue the rest of the pipeline whenever you're ready.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={continuePipelineMutation.isLoading}
              onClick={() => setExpandedStep(pausedAfterStep.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              <EyeIcon className="h-4 w-4" />
              View {pausedAfterStep.name}
            </button>
            <button
              type="button"
              disabled={continuePipelineMutation.isLoading}
              onClick={() => continuePipelineMutation.mutate(pausedAfterStep.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
            >
              {continuePipelineMutation.isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
              Continue pipeline from here
            </button>
          </div>
        </div>
      )}

      {/* YouTube Source Info */}
      {jobData.request?.youtubeUrl && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-lg">
          <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-1">YouTube Source</p>
          <p className="text-sm text-gray-900 dark:text-white truncate">
            {jobData.steps?.[0]?.data?.youtubeTitle || jobData.request.youtubeUrl}
          </p>
          {jobData.steps?.[0]?.data?.youtubeDuration && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Duration: {Math.ceil(jobData.steps[0].data.youtubeDuration / 60)} minutes
            </p>
          )}
        </div>
      )}

      {/* Step Details */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Processing Steps
        </h4>

        {jobData.steps.map((step: any) => {
          // The step we just paused after also opens while awaiting_review,
          // even though the job itself isn't "processing".
          const canExpand = (step.status === 'completed' || step.status === 'failed')
            && (!isProcessing || (isAwaitingReview && step.id === pausedAfterStep?.id));

          return (
            <div key={step.id} className="rounded-lg overflow-hidden border border-transparent">
              {/* Step header row */}
              <div
                className={`flex items-center space-x-3 p-3 ${
                  step.status === 'processing'
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : step.status === 'completed'
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : step.status === 'failed'
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/50'
                } ${canExpand ? 'cursor-pointer hover:opacity-90' : ''} rounded-lg`}
                onClick={() => canExpand && setExpandedStep(step.id)}
              >
                <div className="flex-shrink-0">{getStepIcon(step)}</div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{step.name}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${getStatusColor(step.status)}`}>{step.status}</span>
                      {canExpand && <MagnifyingGlassPlusIcon className="h-4 w-4 text-gray-400" />}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
                  {step.error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">Error: {step.error}</p>}
                  {step.duration && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Completed in {Math.round(step.duration / 1000)}s
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {expandedStep && (
        <StepDetailModal
          jobId={jobId}
          job={jobData}
          step={jobData.steps.find((s: any) => s.id === expandedStep)}
          onClose={() => setExpandedStep(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['video-job', jobId] })}
        />
      )}

      {/* Results */}
      {isCompleted && jobData.result && (
        <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
            Translation Completed Successfully!
          </h4>

          <div className="space-y-2 text-sm text-green-700 dark:text-green-300">
            <p>
              <span className="font-medium">Processing Time:</span>{' '}
              {Math.round(jobData.result.processingTime / 1000)}s
            </p>
            <p>
              <span className="font-medium">Source Language:</span>{' '}
              {jobData.result.sourceLanguage}
            </p>
            <p>
              <span className="font-medium">Target Language:</span>{' '}
              {jobData.result.targetLanguage}
            </p>
            <p>
              <span className="font-medium">Voice:</span>{' '}
              {jobData.result.voiceName}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => videoApi.downloadFile(jobData.result.finalVideoUrl, `translated-video-${jobId}.mp4`)}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Download Translated Video
            </button>
            <button
              type="button"
              onClick={() => setShowPublishModal(true)}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              <VideoCameraIcon className="h-4 w-4 mr-2" />
              Publish to YouTube
            </button>
          </div>
        </div>
      )}

      {showPublishModal && jobData.result && (
        <YouTubePublishModal
          sourceType="translation"
          sourceId={jobData.id}
          defaultTitle={jobData.title || `Translated Video ${jobData.id}`}
          existingUploads={jobData.youtubeUploads || []}
          onClose={() => setShowPublishModal(false)}
          onPublished={() => {
            queryClient.invalidateQueries({ queryKey: ['youtube-uploads'] });
            queryClient.invalidateQueries({ queryKey: ['video-jobs'] });
          }}
        />
      )}

      {/* Error Display */}
      {isFailed && jobData.error && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
            Translation Failed
          </h4>
          <p className="text-sm text-red-700 dark:text-red-300">
            {jobData.error}
          </p>
        </div>
      )}

      {isCancelled && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-2">
            <StopIcon className="h-5 w-5 text-gray-400" />
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Job Cancelled
            </h4>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            This translation job was cancelled at {jobData.progress}% progress.
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoTranslationProgress;